import { useState, useCallback, useMemo } from 'react';

import { sortWithConditions } from 'utils';

import { ColumnProps, TableProps, SortOrder } from '../types';

// only sort after handleSort call
// won't sort the data in first render
export const useSort = <T extends Record<string, any>>({
  columns,
  sortBy,
  onSort,
  originData,
  multiSort,
  disableInnerSort,
}: {
  originData?: T[];
} & Pick<TableProps<T>, 'columns' | 'sortBy' | 'onSort' | 'multiSort' | 'disableInnerSort'>) => {
  const [sortArr, setSortArr] = useState<[string, SortOrder, ColumnProps<T>?][]>(
    sortBy
      ? sortBy.map(([k, v]) => [k, v, columns.find(({ key: ck }) => ck === k)])
      : columns
          .filter(x => x.key && x.defaultSortOrder)
          .map(col => [col.key!, col.defaultSortOrder, col]),
  );

  const handleSort = useCallback(
    (column: ColumnProps<T>) => {
      const { key, sortable, forceSort } = column;

      if (sortable && key) {
        let next = [...sortArr];
        const originKeySortIdx = next?.findIndex(([k]) => k === key);
        if (originKeySortIdx > -1) {
          const sort =
            next[originKeySortIdx][1] === 'asc'
              ? forceSort
                ? 'desc'
                : undefined
              : next[originKeySortIdx][1] === 'desc'
                ? 'asc'
                : 'desc';
          next[originKeySortIdx][1] = sort;

          onSort?.(next);
        } else {
          const sort: SortOrder = 'desc';
          if (multiSort) {
            next.push([key, sort, column]);
          } else {
            next = [[key, sort, column]];
          }

          onSort?.(next);
        }

        setSortArr(next);
      }
    },
    [multiSort, sortArr, onSort],
  );

  const sortState = useMemo(
    () =>
      sortArr?.reduce(
        (m, [key, order]) => ({
          ...m,
          [key]: order,
        }),
        {} as Record<string, SortOrder>,
      ),
    [sortArr],
  );

  const sortedData = useMemo(() => {
    return disableInnerSort ? originData : sortWithConditions(originData, sortArr as any);
  }, [sortArr, originData, disableInnerSort]);

  return {
    sortState,
    handleSort,
    sortedData,
  };
};
